import React from 'react';
import { Edit, Delete, CheckCircle } from '@mui/icons-material';
import { IconButton, Chip, Tooltip } from '@mui/material';
import { IOnboardingDocument, IOnboardingTemplateType } from 'core/interfaces/onboardingInterface';
import styles from '../SchedulerSetting.module.scss';

interface DocumentsListProps {
  documents: IOnboardingDocument[];
  templateTypes: IOnboardingTemplateType[];
  onEditClick: (document: IOnboardingDocument) => void;
  onDeleteClick: (document: IOnboardingDocument) => void;
}

const DocumentsList: React.FC<DocumentsListProps> = ({
  documents,
  templateTypes,
  onEditClick,
  onDeleteClick,
}) => {
  // Extract type ID from IRI and find corresponding label
  const getTypeLabel = (typeIRI: string): string => {
    const match = typeIRI.match(/\/(\d+)$/);
    if (!match) return 'Unknown';
    const typeId = Number(match[1]);
    const type = templateTypes.find(t => t.id === typeId);
    return type?.label || 'Unknown';
  };

  const getTypeColor = (typeIRI: string): 'primary' | 'secondary' | 'default' | 'success' | 'error' | 'info' | 'warning' => {
    const match = typeIRI.match(/\/(\d+)$/);
    if (!match) return 'default';
    const typeId = Number(match[1]);
    switch (typeId) {
      case 1:
        return 'primary';
      case 2:
        return 'secondary';
      case 3:
        return 'info';
      default:
        return 'default';
    }
  };

  if (documents.length === 0) {
    return (
      <div className={styles.emptyState}>
        <p>No onboarding documents configured yet.</p>
        <p>Click &quot;Add Document&quot; to upload a PDF.</p>
      </div>
    );
  }

  return (
    <div className={styles.tableContainer}>
      <table className={styles.workplaceTable}>
        <thead>
          <tr>
            <th>Name</th>
            <th style={{ width: 48 }}>Sort Order</th>
            <th>Type</th>
            <th style={{ width: 120, textAlign: 'center' }}>Manager Signature</th>
            <th style={{ width: 96, textAlign: 'center' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {documents.map((doc) => (
            <tr key={doc.id}>
              <td>
                <strong>{doc.name}</strong>
              </td>
              <td style={{ textAlign: 'center', width: 48 }}>
                <span>{doc.sortOrder}</span>
              </td>
              <td>
                <Chip
                  label={getTypeLabel(doc.type)}
                  color={getTypeColor(doc.type)}
                  size="small"
                />
              </td>
              <td style={{ textAlign: 'center', width: 120 }}>
                {doc.isManagerSignatureRequired ? (
                  <Tooltip title="Manager signature required">
                    <CheckCircle color="success" fontSize="small" />
                  </Tooltip>
                ) : (
                  <span style={{ color: '#999' }}>-</span>
                )}
              </td>
              <td style={{ textAlign: 'center', width: 96 }}>
                <IconButton
                  size="small"
                  title="Edit"
                  onClick={() => onEditClick(doc)}
                  className={styles.iconButton}
                >
                  <Edit className={styles.editIcon} />
                </IconButton>
                <IconButton
                  size="small"
                  title="Delete"
                  onClick={() => onDeleteClick(doc)}
                  className={styles.iconButton}
                  color="error"
                >
                  <Delete />
                </IconButton>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default DocumentsList;
